import * as Yup from 'yup';
import { useEffect, useMemo } from 'react';
import { useSnackbar } from 'notistack';
import { useNavigate, useLocation } from 'react-router-dom';
// form
import { useForm, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { LoadingButton } from '@mui/lab';
import { Box, Button, Card, Grid, Stack, MenuItem, Divider, Typography, Alert } from '@mui/material';

// routes

import useLocales from 'src/hooks/useLocales';
// components

import { observer } from 'mobx-react-lite';
import { IElectricMeter } from 'src/@types/Choqorak/Settings/ElectricMeter';
import { FormProvider, RHFTextField, RHFSelect } from 'src/components/hook-form';
import Iconify from 'src/components/Iconify';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { useStore } from 'src/stores/store';
import useThrottle from 'src/hooks/useThrottle';
// ----------------------------------------------------------------------

export default observer(function ElectricMeterNewEditForm() {
  const { ElectricMeterStore, ChoqorakCommonDropdown } = useStore();
  const { translate } = useLocales();
  const {
    createElectricMeter,
    updateElectricMeter,
    editMode,
    selectedElectricMeter,
    clearSelectedElectricMeter,
  } = ElectricMeterStore;
  const {
    loadElectricBoxeDDL,
    ElectricBoxOption,
    ElectricityPriceOption,
    loadElectricityPriceDDL,
  } = ChoqorakCommonDropdown;
  const navigate = useNavigate();

  const { enqueueSnackbar } = useSnackbar();

  const NewElectricMeterSchema = Yup.object().shape({
    electricBoxId: Yup.string().required(`${translate('Validation.electricBoxId')}`),
    electricityPriceId: Yup.string().required(`${translate('Validation.electricityPriceId')}`),
    electricMeterSerial: Yup.string().required(`${translate('Validation.electricMeterSerial')}`),
  });

  const defaultValues = useMemo<IElectricMeter>(
    () => ({
      electricMeterId: selectedElectricMeter?.electricMeterId,
      electricBoxId: selectedElectricMeter?.electricBoxId || undefined,
      electricityPriceId: selectedElectricMeter?.electricityPriceId || undefined,
      lastElectricMeterDegree: selectedElectricMeter?.lastElectricMeterDegree || undefined,
      electricMeterSerial: selectedElectricMeter?.electricMeterSerial || '',
    }),
    [selectedElectricMeter]
  );

  const methods = useForm<IElectricMeter>({
    resolver: yupResolver(NewElectricMeterSchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    control,
    setError,
    formState: { isSubmitting ,errors},
  } = methods;

  const onSubmit =useThrottle(   (data: IElectricMeter) => {
    if (!editMode) {
      ///create
      createElectricMeter(data).then(() => {
        reset();
        enqueueSnackbar(`${translate('Tostar.CreateSuccess')}`);
        navigate(PATH_DASHBOARD.ElectricMeter.list);
      }).catch((err) => {
        var json = JSON.parse(err.request.response);
        if (json.error != null) {
          setError('afterSubmit', { ...err, message: json.message });
        }
     
      });
    } else {
      ///update
      updateElectricMeter(data).then(() => {
        reset();
        enqueueSnackbar(`${translate('Tostar.UpdateSuccess')}`);
        navigate(PATH_DASHBOARD.ElectricMeter.list);
      }).catch((err) => {
        var json = JSON.parse(err.request.response);
        if (json.error != null) {
          setError('afterSubmit', { ...err, message: json.message });
        }
     
      });
    }
  });

  useEffect(() => {
    loadElectricityPriceDDL();
    loadElectricBoxeDDL();
    if (editMode) {
      reset(defaultValues);
    }
    if (!editMode) {
      reset(defaultValues);
    }
  }, [reset, editMode, defaultValues]);

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        {!!errors.afterSubmit && (
        <Alert sx={{ mb: 2 }} severity="error">
          {errors.afterSubmit.message}
        </Alert>
      )}
      <Grid container spacing={3}>
        <Grid item xs={12} md={12}>
          <Card sx={{ p: 3 }}>
            <Box sx={{ p: 3 }}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                sx={{ width: 1 }}
                alignContent="flex-end"
              >
                <RHFSelect
                  name="electricBoxId"
                  label={translate('ElectricMeter.ElectricBoxCode')}
                  size="medium"
                  InputLabelProps={{ shrink: true }}
                  SelectProps={{ native: false, sx: { textTransform: 'capitalize' } }}
                  sx={{ maxWidth: { md: '100%' } }}
                >
                  {ElectricBoxOption.map((option) => (
                    <MenuItem
                      key={option.value}
                      value={option.value}
                      sx={{
                        mx: 1,
                        my: 0.5,
                        borderRadius: 0.75,
                        typography: 'body2',
                        textTransform: 'capitalize',
                      }}
                    >
                      {option.text}
                    </MenuItem>
                  ))}
                </RHFSelect>

                <RHFTextField
                  size="medium"
                  name="electricMeterSerial"
                  label={translate('ElectricMeter.ElectricMeterSerial')}
                  sx={{ maxWidth: { md: '100%' } }}
                />
                {editMode &&(
                 <RHFTextField
                  size="medium"
                  name="lastElectricMeterDegree"
                  label={translate('ElectricMeter.LastElectricMeterDegree')}
                  sx={{ maxWidth: { md: '100%' } }}
                />)}
                <RHFSelect
                  name="electricityPriceId"
                  label={translate('ElectricMeter.ElectricityPrice')}
                  size="medium"
                  InputLabelProps={{ shrink: true }}
                  SelectProps={{ native: false, sx: { textTransform: 'capitalize' } }}
                  sx={{ maxWidth: { md: '100%' } }}
                >
                  {ElectricityPriceOption.map((option) => (
                    <MenuItem
                      key={option.value}
                      value={option.value}
                      sx={{
                        mx: 1,
                        my: 0.5,
                        borderRadius: 0.75,
                        typography: 'body2',
                        textTransform: 'capitalize',
                      }}
                    >
                      {option.text}
                    </MenuItem>
                  ))}
                </RHFSelect>
              </Stack>

              <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
                <LoadingButton
                  fullWidth
                  type="submit"
                  variant="contained"
                  loading={isSubmitting}
                  startIcon={
                    !editMode ? <Iconify icon="eva:plus-fill" /> : <Iconify icon="eva:edit-fill" />
                  }
                >
                  {!editMode ? `${translate('CRUD.Save')}` : `${translate('CRUD.Update')}`}
                </LoadingButton>
                <Button
                  fullWidth
                  variant="contained"
                  color="error"
                  startIcon={<Iconify icon="eva:arrow-ios-back-fill" />}
                  onClick={() => {
                    clearSelectedElectricMeter();
                    navigate(PATH_DASHBOARD.ElectricMeter.list);
                  }}
                >
                  {translate('CRUD.BackToList')}
                </Button>
              </Stack>
            </Box>
          </Card>
        </Grid>
      </Grid>
    </FormProvider>
  );
});
